import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

// Inactivity timeout — 30 minutes
const INACTIVITY_MS = 30 * 60 * 1000;
// Warning shown 2 minutes before logout
const WARN_BEFORE_MS = 2 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user,               setUser]               = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading,            setLoading]            = useState(true);
  const [sessionWarning,     setSessionWarning]     = useState(false);

  const inactivityTimer = useRef(null);
  const warnTimer       = useRef(null);

  // ── Boot — restore session ─────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem('jengo_user');
    const token  = localStorage.getItem('jengo_token');
    const mcp    = localStorage.getItem('jengo_mcp') === '1';
    if (stored && token) {
      setUser(JSON.parse(stored));
      setMustChangePassword(mcp);
    }
    setLoading(false);
  }, []);

  // ── Session timeout ────────────────────────────────
  const resetTimers = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warnTimer.current);
    setSessionWarning(false);

    if (!user) return;

    warnTimer.current = setTimeout(() => {
      setSessionWarning(true);
    }, INACTIVITY_MS - WARN_BEFORE_MS);

    inactivityTimer.current = setTimeout(() => {
      setSessionWarning(false);
      logout();
    }, INACTIVITY_MS);
  }, [user]); // eslint-disable-line

  // Attach activity listeners when logged in
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown','mousemove','keydown','scroll','touchstart','click'];
    events.forEach(e => window.addEventListener(e, resetTimers, { passive:true }));
    resetTimers(); // start timers
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimers));
      clearTimeout(inactivityTimer.current);
      clearTimeout(warnTimer.current);
    };
  }, [user, resetTimers]);

  // ── Auth actions ───────────────────────────────────
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('jengo_token', data.token);
    localStorage.setItem('jengo_user',  JSON.stringify(data.user));
    localStorage.setItem('jengo_mcp',   data.must_change_password ? '1' : '0');
    setUser(data.user);
    setMustChangePassword(Boolean(data.must_change_password));
    return data.user;
  }, []);

  const clearMustChange = useCallback(() => {
    setMustChangePassword(false);
    localStorage.setItem('jengo_mcp', '0');
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('jengo_token');
    localStorage.removeItem('jengo_user');
    localStorage.removeItem('jengo_mcp');
    localStorage.removeItem('jengo_ghost_token');
    localStorage.removeItem('jengo_ghost_tenant');
    localStorage.removeItem('jengo_pre_ghost_token');
    localStorage.removeItem('jengo_pre_ghost_user');
    setUser(null);
    setMustChangePassword(false);
    clearTimeout(inactivityTimer.current);
    clearTimeout(warnTimer.current);
    window.location.href = '/login';
  }, []);

  // ── Ghost session — return to SA ───────────────────
  const isGhost = user?._ghost;

  const returnToSA = useCallback(async () => {
    const ghostToken    = localStorage.getItem('jengo_ghost_token');
    const preGhostToken = localStorage.getItem('jengo_pre_ghost_token');
    const preGhostUser  = localStorage.getItem('jengo_pre_ghost_user');

    // End ghost session on server
    if (ghostToken) {
      try { await api.post('/auth/ghost-end', { ghostToken }); } catch {}
    }

    // Restore SA session
    if (preGhostToken && preGhostUser) {
      localStorage.setItem('jengo_token', preGhostToken);
      localStorage.setItem('jengo_user',  preGhostUser);
      localStorage.removeItem('jengo_ghost_token');
      localStorage.removeItem('jengo_ghost_tenant');
      localStorage.removeItem('jengo_pre_ghost_token');
      localStorage.removeItem('jengo_pre_ghost_user');
      setUser(JSON.parse(preGhostUser));
      window.location.href = '/sa/dashboard';
    } else {
      logout();
    }
  }, [logout]);

  // ── Screenshot detection ───────────────────────────
  useEffect(() => {
    if (!user) return;
    const handleKey = (e) => {
      if (e.key === 'PrintScreen' || (e.metaKey && e.shiftKey && ['3','4'].includes(e.key))) {
        api.post('/audit/screenshot', { module:document.title, page:window.location.pathname, trigger:'keypress' }).catch(()=>{});
      }
    };
    const handlePrint = () => {
      api.post('/audit/screenshot', { module:document.title, page:window.location.pathname, trigger:'print_dialog' }).catch(()=>{});
    };
    window.addEventListener('keyup', handleKey);
    window.addEventListener('beforeprint', handlePrint);
    return () => {
      window.removeEventListener('keyup', handleKey);
      window.removeEventListener('beforeprint', handlePrint);
    };
  }, [user]);

  const isSA = user?.role === 'super_admin';
  const isOA = user?.is_oa;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isSA, isOA, isGhost, returnToSA,
      mustChangePassword, clearMustChange,
      sessionWarning, resetTimers,
    }}>
      {/* Session warning banner */}
      {sessionWarning && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:9999,
          background:'var(--warning)', color:'#fff',
          padding:'10px 20px', display:'flex',
          alignItems:'center', justifyContent:'space-between',
          fontSize:14, fontWeight:500, fontFamily:'DM Sans, sans-serif',
        }}>
          <span>⏱ Your session will expire in 2 minutes due to inactivity.</span>
          <button
            onClick={resetTimers}
            style={{background:'rgba(255,255,255,0.25)',border:'none',color:'#fff',
              padding:'6px 14px',borderRadius:6,cursor:'pointer',fontWeight:600,fontSize:13}}
          >
            Stay logged in
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
